/**
 * Build a Runloop platform dashboard URL for a devbox.
 *
 * @param devboxId - The devbox ID (e.g. "dbx_33Gfvbx7XytShkDYuUbI5")
 * @param platformUrl - Server-provided platform URL from /api/config
 * @returns Full dashboard URL, or null if no valid URL can be constructed
 */
export function getRunloopDevboxUrl(
  devboxId: string | null | undefined,
  platformUrl: string | null | undefined,
): string | null {
  if (!devboxId) return null;

  // Use server-provided platform URL if available
  if (platformUrl) {
    const base = platformUrl.replace(/\/+$/, '');
    return `${base}/devboxes/${encodeURIComponent(devboxId)}`;
  }

  // Fallback: derive platform host from current browser hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const platformHost = derivePlatformHost(hostname);
    if (platformHost) {
      return `https://${platformHost}/devboxes/${encodeURIComponent(devboxId)}`;
    }
  }

  return null;
}

/**
 * Derive the Runloop platform host from an app hostname.
 * Maps *.runloop.ai -> platform.runloop.ai
 * Maps *.runloop.pro -> platform.runloop.pro
 */
function derivePlatformHost(hostname: string): string | null {
  // Match subdomains of runloop.ai or runloop.pro
  const match = hostname.match(/\.runloop\.(ai|pro)$/);
  if (match) {
    const tld = match[1]; // 'ai' or 'pro'
    return `platform.runloop.${tld}`;
  }
  return null;
}
